// RsrcContext.js
import axios from 'axios';
import React, { createContext, useContext } from 'react';
import { useAlert } from '../alert/AlertContext';
import { usePrincipal } from '../principal/PrincipalContext';
import { ExprUtils } from '../utils/ExprUtils';

const RsrcContext = createContext(null);
/**
 * RsrcProvider component provides resource functions via context.
 * @param {{ children: React.ReactNode }} props
 */
export const RsrcProvider = ({ children }) => {
  const { org, getToken, login } = usePrincipal();
  const { showAlert } = useAlert();

  const api = '/api/rest/';

  // Create an axios instance for RsrcContext API calls
  const axiosInstance = axios.create();

  const authHeaders = () => {
    const token = getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Add a request interceptor to set the Authorization header with the Bearer token.
  axiosInstance.interceptors.request.use((config) => {
    const headers = authHeaders();
    if (headers.Authorization) {
      config.headers['Authorization'] = headers.Authorization;
    }
    return config;
  });

  // Add a response interceptor to handle errors.
  axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      const errorStatus = error.response?.status;
      const errorUrl = error.config?.url;
      let errorMsg = error.response?.data;
      if (errorMsg != null && errorMsg.error != null) {
        errorMsg = errorMsg.error;
      }

      console.log('Error occurred:', errorStatus, errorUrl, errorMsg);

      if (errorStatus === 503) {
        console.log(`All ${errorStatus} errors at ${errorUrl} can be safely ignored, module is not available.`);
      } else if (errorStatus === 404) {
        console.log(`Unable to find ${errorUrl}. This is a programming error.`);
      } else if (errorStatus === 401) {
        login();
      } else if (errorStatus === 403) {
        showAlert(`${errorUrl} forbidden. Kernel support has been notified.`, 'error');
      } else {
        showAlert(`${errorMsg}. Kernel support has been notified.`, 'error');
      }

      // Prevent subscriptions from executing after an error.
      return new Promise(() => { });
    }
  );

  /**
   * Builds the base API path based on the module, current org, and resource.
   * @param {string} module - The module name.
   * @param {string} rsrc - The resource name.
   * @returns {string} The full API endpoint.
   * @throws {Error} Throws error if organization is not defined.
   */
  const rsrcApi = (module, rsrc) => {
    if (!org) {
      throw new Error('Organization is not defined in PrincipalContext');
    }
    return `/${module}${api}${org}/${rsrc}/`;
  };

  // API Methods using axiosInstance (headers added automatically)

  /**
   * Retrieves an entity definition.
   * @param {string} module - The module name.
   * @param {string} entity - The entity name.
   * @returns {Promise<any>}
   */
  const entityDef = (module, entity) => {
    return axiosInstance.get(`${rsrcApi(module, entity)}entityDef`).then(res => res.data);
  };

  /**
   * Retrieves field definitions for an entity.
   * @param {string} module - The module name.
   * @param {string} entity - The entity name.
   * @returns {Promise<any>}
   */
  const fieldDefs = (module, entity) => {
    return axiosInstance.get(`${rsrcApi(module, entity)}fieldDefs`).then(res => res.data);
  };

  /**
   * Retrieves plugin field definitions.
   * @param {string} module - The module name.
   * @param {string} pluginCode - The plugin code.
   * @param {string} entity - The entity name.
   * @returns {Promise<any>}
   */
  const pluginFieldDefs = (module, pluginCode, entity) => {
    return axiosInstance
      .get(`${rsrcApi(module, pluginCode)}pluginFieldDefs/${entity ?? 'NONE'}`)
      .then(res => res.data);
  };

  /**
   * Retrieves a single record by its ID.
   * @param {string} module - The module name.
   * @param {string} entity - The entity name.
   * @param {any} id - The identifier.
   * @returns {Promise<any>}
   */
  const byId = (module, entity, id) => {
    const expr = ExprUtils.whereParams('id', id);
    const rowsReq = { expr };
    return one(module, entity, rowsReq);
  };

  /**
   * Retrieves the first record from entity rows.
   * @param {string} module - The module name.
   * @param {string} entity - The entity name.
   * @param {any} rowsReq - The request parameters.
   * @returns {Promise<any>}
   */
  const one = (module, entity, rowsReq) => {
    return rows(module, entity, rowsReq).then((storables) => {
      return (!storables || storables.length === 0) ? null : storables[0];
    });
  };

  const rows = (module, rsrc, rowsReq) => {
    rowsReq.augFks = true;
    console.log(`Preparing Entity Rows for ${module}/${rsrc} with request`, rowsReq);
    return axiosInstance
      .post(`${rsrcApi(module, rsrc)}rows`, rowsReq)
      .then(res => res.data);
  };


  /**
   * Retrieves the count of records for an entity.
   * @param {string} module - The module name.
   * @param {string} entity - The entity name.
   * @param {any} rowsReq - The request parameters.
   * @returns {Promise<any>}
   */
  const rowCount = (module, rsrc, rowsReq) => {
    console.log(`Entity Count for ${module}/${rsrc} with request`, rowsReq);
    return axiosInstance
      .post(`${rsrcApi(module, rsrc)}rowCount`, rowsReq)
      .then(res => res.data);
  };

  /**
   * Executes a search request.
   * @param {string} module - The module name.
   * @param {string} entity - The entity name.
   * @param {any} reqParam - The search parameters.
   * @returns {Promise<any>}
   */
  const search = (module, entity, reqParam) => {
    return axiosInstance
      .post(`${rsrcApi(module, entity)}search`, reqParam)
      .then(res => res.data);
  };

  /**
   * Retrieves detailed information about an entity.
   * @param {string} module - The module name.
   * @param {string} entity - The entity name.
   * @param {any} id - The entity identifier.
   * @returns {Promise<any>}
   */
  const details = (module, entity, id) => {
    return axiosInstance
      .get(`${rsrcApi(module, entity)}details/${id}`)
      .then(res => res.data);
  };

  /**
   * Sends data to the server.
   * @param {string} module - The module name.
   * @param {string} pluginCode - The plugin code.
   * @param {any} reqParam - The request parameters.
   * @param {string} [entity] - Optional entity parameter.
   * @returns {Promise<any>}
   */
  const data = (module, pluginCode, reqParam = {}, entity) => {
    return axiosInstance
      .post(`${rsrcApi(module, pluginCode)}data/${entity ?? 'NONE'}`, reqParam)
      .then(res => res.data);
  };

  /**
   * Retrieves Excel table data.
   * @param {string} module - The module name.
   * @param {string} entity - The entity name.
   * @param {any} reqParam - The request parameters.
   * @returns {Promise<any>}
   */
  const excelTables = (module, entity, reqParam) => {
    return axiosInstance
      .post(`${rsrcApi(module, entity)}excelTables`, reqParam)
      .then(res => res.data);
  };

  /**
   * Retrieves properties.
   * @param {string} module - The module name.
   * @param {string} entity - The entity name.
   * @param {any} reqParam - The request parameters.
   * @returns {Promise<any>}
   */
  const props = (module, entity, reqParam) => {
    return axiosInstance
      .post(`${rsrcApi(module, entity)}props`, reqParam)
      .then(res => res.data);
  };

  /**
   * Saves entity data.
   * @param {string} module - The module name.
   * @param {string} entity - The entity name.
   * @param {any} data - The data to save.
   * @returns {Promise<any>} The saved entity (or entities).
   */
  const save = (module, entity, data) => {
    let entityObjs = Array.isArray(data) ? data : [data];
    return axiosInstance
      .post(`${rsrcApi(module, entity)}save`, entityObjs)
      .then(res => {
        const storables = res.data;
        return Array.isArray(data) ? storables : storables[0];
      });
  };

  /**
   * Stores plugin data.
   * @param {string} module - The module name.
   * @param {string} pluginCode - The plugin code.
   * @param {any} reqParam - The request parameters.
   * @returns {Promise<any>}
   */
  const store = (module, pluginCode, reqParam) => {
    return axiosInstance
      .post(`${rsrcApi(module, pluginCode)}store`, reqParam)
      .then(res => res.data);
  };

  /**
   * Clones an entity.
   * @param {string} module - The module name.
   * @param {string} entity - The entity name.
   * @param {any} origId - The original entity ID.
   * @param {any} [req] - Optional request data.
   * @returns {Promise<any>}
   */
  const clone = (module, entity, origId, req) => {
    req = req ?? {};
    return axiosInstance
      .post(`${rsrcApi(module, entity)}clone/${origId}`, req)
      .then(res => res.data);
  };

  /**
   * Deletes an entity.
   * @param {string} module - The module name.
   * @param {string} entity - The entity name.
   * @param {any} id - The entity identifier.
   * @returns {Promise<any>}
   */
  const _delete = (module, entity, id) => {
    const req = { id };
    return axiosInstance
      .post(`${rsrcApi(module, entity)}delete/${id}`, req)
      .then(res => res.data);
  };

  /**
   * Archives an entity.
   * @param {string} module - The module name.
   * @param {string} entity - The entity name.
   * @param {any} id - The entity identifier.
   * @returns {Promise<any>}
   */
  const archive = (module, entity, id) => {
    const req = { id };
    return axiosInstance
      .post(`${rsrcApi(module, entity)}archive`, req)
      .then(res => res.data);
  };


  /**
   * Submits a file
   * @param {string} module - The module name.
   * @param {string} pluginCode - The plugin (subReader) code.
   * @param {FormData} formData - The FormData object containing file and reqParam.
   * @param {string} entity - The entity name.
   * @returns {Promise<any>}
   */
  const upload = (module, pluginCode, formData, entity) => {
    return axiosInstance
      .post(`${rsrcApi(module, pluginCode, entity)}upload/${entity ?? 'NONE'}`, formData)
      .then(res => res.data);
  };

  /**
   * Retrieves options for an entity.
   * @param {string} module - The module name.
   * @param {string} entity - The entity name.
   * @returns {Promise<any>}
   */
  const options = (module, entity) => {
    return axiosInstance.get(`${rsrcApi(module, entity)}options`).then(res => res.data);
  };

  /**
   * Starts a job.
   * @param {any} jobId - The job identifier.
   * @returns {Promise<any>}
   */
  const start = (jobId) => {
    return axiosInstance
      .post(`${rsrcApi('skdr', 'JOB')}start/${jobId}`, {})
      .then(res => res.data);
  };

  // Viewer-specific methods

  /**
   * Retrieves display information.
   * @param {string} module - The module name.
   * @param {string} pluginCode - The plugin code.
   * @param {any} reqParam - The request parameters.
   * @param {string} [entity] - Optional entity parameter.
   * @returns {Promise<any>}
   */
  const viewerFieldDefs = (module, pluginCode, reqParam, entity) => {
    return axiosInstance
      .post(`${rsrcApi(module, pluginCode)}viewerFieldDefs/${entity ?? 'NONE'}`, reqParam)
      .then(res => res.data);
  };

  /**
   * Retrieves counts.
   * @param {string} module - The module name.
   * @param {string} pluginCode - The plugin code.
   * @param {any} reqParam - The request parameters.
   * @param {string} [entity] - Optional entity parameter.
   * @returns {Promise<any>}
   */
  const viewerCount = (module, pluginCode, reqParam, entity) => {
    return axiosInstance
      .post(`${rsrcApi(module, pluginCode)}viewerCount/${entity ?? 'NONE'}`, reqParam)
      .then(res => res.data);
  };

  /**
   * Fetches data.
   * @param {string} module - The module name.
   * @param {string} pluginCode - The plugin code.
   * @param {any} reqParam - The request parameters.
   * @param {string} [entity] - Optional entity parameter.
   * @returns {Promise<any>}
   */
  const viewerRows = (module, pluginCode, reqParam, entity) => {
    return axiosInstance
      .post(`${rsrcApi(module, pluginCode)}viewerRows/${entity ?? 'NONE'}`, reqParam)
      .then(res => res.data);
  };

  /**
   * Retrieves totals.
   * @param {string} module - The module name.
   * @param {string} pluginCode - The plugin code.
   * @param {any} reqParam - The request parameters.
   * @param {string} [entity] - Optional entity parameter.
   * @returns {Promise<any>}
   */
  const viewerAggTotals = (module, pluginCode, reqParam, entity) => {
    return axiosInstance
      .post(`${rsrcApi(module, pluginCode)}viewerAggTotals/${entity ?? 'NONE'}`, reqParam)
      .then(res => res.data);
  }

  /**
   * Retrieves a log file.
   * @param {string} module - The module name.
   * @param {number} dayOffset - The day offset.
   * @returns {Promise<any>}
   */
  const logfile = (module, dayOffset) => {
    return axiosInstance
      .get(`/${module}${api}LogFile/${dayOffset}`, { responseType: 'text' })
      .then(res => res.data);
  };

  /**
   * Retrieves enumerations.
   * @param {string} module - The module name.
   * @param {string} enumCode - The enumeration code.
   * @param {string} [filterMethod='NONE'] - Optional filter method.
   * @param {any} [reqParam={}] - Optional request parameters.
   * @returns {Promise<any>}
   */
  const enums = (module, enumCode, filterMethod = 'NONE', reqParam = {}) => {
    return axiosInstance
      .post(`${rsrcApi(module, 'ENUM')}enum/${enumCode}/${filterMethod}`, reqParam)
      .then(res => res.data);
  };

  /**
   * Retrieves documentation.
   * @param {string} module - The module name.
   * @returns {Promise<any>}
   */
  const docs = (module) => {
    const reqParam = { audience: 'UG' };
    return axiosInstance
      .post(`${rsrcApi(module, 'DOCS')}`, reqParam)
      .then(res => res.data);
  };

  /**
   * Retrieves help data.
   * @param {string} module - The module name.
   * @returns {Promise<any>}
   */
  const help = (module) => {
    return axiosInstance
      .get(`${rsrcApi(module, 'HELP')}help`)
      .then(res => res.data);
  };

  /**
   * Sends a chat prompt.
   * @param {string} prompt - The chat prompt.
   * @returns {Promise<any>}
   */
  const chat = (prompt) => {
    const reqParam = { prompt };
    return axiosInstance
      .post(`${rsrcApi('admin', 'CHAT')}prompt`, reqParam)
      .then(res => res.data);
  };

  /**
   * Initializes events.
   * @returns {Promise<any>}
   */
  const initEvents = () => {
    return axiosInstance
      .get(`${rsrcApi('skdr', 'EVENT')}init`)
      .then(res => res.data);
  };

  /**
   * Initiates a file download.
   * @param {string} module - The module where the Export resides
   * @param {string} exportId - The ID of the Export we are downloading
   */
  const download = (module, exportId) => {
    axiosInstance.get(`${rsrcApi(module, exportId)}download`, { responseType: 'blob', observe: 'response' }).then((response) => {
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = getFilenameFromHeaders(response.headers) || 'file';
      anchor.click();
      URL.revokeObjectURL(url);
    });
  };

  /**
   * Extracts the filename from response headers.
   * @param {any} headers - The response headers.
   * @returns {string|null} The filename, or null if not found.
   */
  const getFilenameFromHeaders = (headers) => {
    const contentDisposition = headers['content-disposition'];
    if (!contentDisposition) return null;
    const match = contentDisposition.match(/filename=([^;]+)/);
    return match ? match[1] : null;
  };

  // Actuator API Methods

  /**
   * Returns system, JVM, and HTTP metrics.
   * @param {string} module - The module to query metrics for. 
   * @returns 
   */
  const metrics = (module) => {
    return axiosInstance.get(`${rsrcApi(module, 'METRICS')}metrics`).then(res => res.data);
  };

  /**
   * Returns build and environment info from the actuator info endpoint.
   * @param {string} module - The module to query info for.
   * @returns {Promise<any>}
   */
  const info = (module) => {
    return axiosInstance.get(`${rsrcApi(module, 'INFO')}info`).then(res => res.data);
  };

  /**
   * Returns available loggers and their levels.
   * @param {string} module - The module to query loggers for.
   * @returns {Promise<any>}
   */
  const loggers = (module) => {
    return axiosInstance.get(`${rsrcApi(module, 'LOGGERS')}loggers`).then(res => res.data);
  };

  /**
   * Updates a logger level.
   * @param {string} module - The module to update.
   * @param {string} loggerName - The logger name.
  * @param {string|null} configuredLevel - The configured level (null to inherit).
   * @returns {Promise<any>}
   */
  const updateLogger = (module, loggerName, configuredLevel) => {
    return axiosInstance
      .post(
        `${rsrcApi(module, 'LOGGERS')}save/${loggerName}`,
        configuredLevel ? JSON.stringify(configuredLevel) : undefined,
        { headers: { 'Content-Type': 'application/json' } }
      )
      .then(res => res.data);
  };

  // Expose all functions via context.
  /** @type {RsrcContextType} */
  const value = {
    entityDef,
    fieldDefs,
    pluginFieldDefs,
    byId,
    one,
    rows,
    rowCount,
    search,
    details,
    data,
    excelTables,
    props,
    save,
    store,
    clone,
    delete: _delete,
    archive,
    upload,
    options,
    start,
    viewerFieldDefs,
    viewerRows,
    viewerCount,
    viewerAggTotals,
    logfile,
    enums,
    docs,
    chat,
    initEvents,
    download,
    help,
    // Actuator
    metrics,
    info,
    loggers,
    updateLogger,
    authHeaders,

  };

  return (
    <RsrcContext.Provider value={value}>
      {children}
    </RsrcContext.Provider>
  );
};

/**
 * Custom hook to consume the Rsrc context.
 * @returns {RsrcContextType} The resource functions.
 */
export const useRsrc = () => useContext(RsrcContext);

/**
 * @typedef {Object} RsrcContextType
 * @property {(module: string, entity: string) => Promise<any>} entityDef - Gets the entity definition.
 * @property {(module: string, entity: string) => Promise<any>} fieldDefs - Retrieves the field definitions.
 * @property {(module: string, pluginCode: string, entity?: string) => Promise<any>} pluginFieldDefs - Retrieves plugin-specific field definitions.
 * @property {(module: string, entity: string, id: any) => Promise<any>} byId - Gets a single record by ID.
 * @property {(module: string, entity: string, rowsReq: any) => Promise<any>} one - Retrieves the first record from the entity rows.
 * @property {(module: string, rsrc: string, rowsReq: any) => Promise<any>} rows - Retrieves entity/query rows.
 * @property {(module: string, rsrc: string, rowsReq: any) => Promise<any>} rowCount - Retrieves the count of entity/query rows.
 * @property {(module: string, entity: string, reqParam: any) => Promise<any>} search - Executes a search.
 * @property {(module: string, entity: string, id: any) => Promise<any>} details - Retrieves details of an entity.
 * @property {(module: string, pluginCode: string, reqParam: any, entity?: string) => Promise<any>} data - Posts data to an endpoint.
 * @property {(module: string, entity: string, reqParam: any) => Promise<any>} excelTables - Retrieves Excel table data.
 * @property {(module: string, entity: string, reqParam: any) => Promise<any>} props - Retrieves properties.
 * @property {(module: string, entity: string, data: any) => Promise<any>} save - Saves entity data.
 * @property {(module: string, pluginCode: string, reqParam: any) => Promise<any>} store - Stores plugin data.
 * @property {(module: string, entity: string, origId: any, req?: any) => Promise<any>} clone - Clones an entity.
 * @property {(module: string, entity: string, id: any) => Promise<any>} delete - Deletes an entity.
 * @property {(module: string, entity: string, id: any) => Promise<any>} archive - Archives an entity.
 * @property {(module: string, pluginCode: string, formData: any, entity?: string, entityId?: any) => Promise<any>} upload - Submits form data.
 * @property {(module: string, entity: string) => Promise<any>} options - Retrieves options for an entity.
 * @property {(jobId: any) => Promise<any>} start - Starts a job.
 * @property {(module: string, pluginCode: string, reqParam: any, entity?: string) => Promise<any>} viewerFieldDefs - Retrieves field definitions for a viewer
 * @property {(module: string, pluginCode: string, reqParam: any, entity?: string) => Promise<any>} viewerRows - Retrieves rows for a viewer
 * @property {(module: string, pluginCode: string, reqParam: any, entity?: string) => Promise<any>} viewerCount - Retrieves the count for a viewer
 * @property {(module: string, pluginCode: string, reqParam: any, entity?: string) => Promise<any>} viewerAggTotals - Retrieves the totals for a viewer
 * @property {(module: string, dayOffset: number) => Promise<any>} logfile - Retrieves a log file.
 * @property {(module: string, enumCode: string, filterMethod?: string, reqParam?: any) => Promise<any>} enums - Retrieves enumeration data.
 * @property {(module: string) => Promise<any>} docs - Retrieves documentation.
 * @property {(module: string) => Promise<any>} help - Retrieves help data.
 * @property {(prompt: string) => Promise<any>} chat - Posts a chat prompt.
 * @property {() => Promise<any>} initEvents - Initializes events.
 * @property {(module: string, exportId: exportId) => void} download - Initiates a file download.
 * 
 * @property {(module: string) => Promise<any>} metrics - Retrieves metrics for a module.
  * @property {(module: string) => Promise<any>} loggers - Retrieves loggers and levels for a module.
  * @property {(module: string, loggerName: string, configuredLevel: string|null) => Promise<any>} updateLogger - Updates a logger level.
 * @property {() => ({ Authorization?: string })} authHeaders - Returns auth headers for API requests.
 */